import { Link, useRouterState } from '@tanstack/react-router';
import {
  LogOut,
  User,
  Menu,
  LayoutDashboard,
  PlayCircle,
  PlusCircle,
  Settings,
  Package,
  Users,
  FileText,
  Building2,
  GitBranch,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks';
import { cn } from '@/lib/utils';
import { useState } from 'react';
import { hasPermission, type Permission } from '@/types';

export function Header() {
  const { user, isAuthenticated, login, logout, isLoading } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="flex h-14 items-center px-4 lg:px-6">
        {/* Mobile menu button */}
        <Button
          variant="ghost"
          size="icon"
          className="lg:hidden mr-2"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          aria-label="Toggle menu"
        >
          <Menu className="h-5 w-5" />
        </Button>

        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 font-semibold">
          <div className="h-6 w-6 rounded bg-primary flex items-center justify-center">
            <span className="text-primary-foreground text-xs font-bold">M</span>
          </div>
          <span className="hidden sm:inline">Migration Orchestrator</span>
        </Link>

        {/* Spacer */}
        <div className="flex-1" />

        {/* User menu */}
        <div className="flex items-center gap-2">
          {isAuthenticated && user ? (
            <div className="flex items-center gap-4">
              <div className="hidden sm:flex items-center gap-2">
                <div className="flex items-center justify-center h-8 w-8 rounded-full bg-primary">
                  <User className="h-4 w-4 text-primary-foreground" />
                </div>
                <span className="text-sm font-medium">{user.name}</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={logout}
                disabled={isLoading}
              >
                <LogOut className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">Sign Out</span>
              </Button>
            </div>
          ) : (
            <Button onClick={login} disabled={isLoading}>
              {isLoading ? 'Signing in...' : 'Sign In'}
            </Button>
          )}
        </div>
      </div>

      {/* Mobile menu overlay */}
      {isMenuOpen && (
        <MobileMenuOverlay onClose={() => setIsMenuOpen(false)} />
      )}
    </header>
  );
}

interface MobileMenuOverlayProps {
  onClose: () => void;
}

function MobileMenuOverlay({ onClose }: MobileMenuOverlayProps) {
  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-black/50 lg:hidden"
        onClick={onClose}
      />
      <nav className="fixed inset-y-0 left-0 z-50 w-64 bg-background border-r lg:hidden pt-14">
        <NavigationLinks onLinkClick={onClose} />
      </nav>
    </>
  );
}

interface MobileNavItem {
  label: string;
  to: string;
  icon: typeof LayoutDashboard;
  requiredPermission: Permission;
}

const mainMobileItems: MobileNavItem[] = [
  { label: 'Dashboard', to: '/', icon: LayoutDashboard, requiredPermission: 'read' },
  { label: 'Migrations', to: '/migrations', icon: PlayCircle, requiredPermission: 'read' },
  { label: 'New Migration', to: '/migrations/new', icon: PlusCircle, requiredPermission: 'write' },
];

const adminMobileItems: MobileNavItem[] = [
  { label: 'Settings', to: '/admin/settings', icon: Settings, requiredPermission: 'admin' },
  { label: 'Modules', to: '/admin/modules', icon: Package, requiredPermission: 'admin' },
  { label: 'Users', to: '/admin/users', icon: Users, requiredPermission: 'admin' },
  { label: 'Audit Log', to: '/admin/audit', icon: FileText, requiredPermission: 'admin' },
];

const toolsMobileItems: MobileNavItem[] = [
  // Read-only diagnostics — available to Viewers (Readers) as well as Operators/Admins.
  { label: 'Amplify Org', to: '/tools/amplify-org', icon: Building2, requiredPermission: 'read' },
  { label: 'Bridge Analysis', to: '/tools/bridge-analysis', icon: GitBranch, requiredPermission: 'read' },
];

interface NavigationLinksProps {
  onLinkClick?: () => void;
}

function NavigationLinks({ onLinkClick }: NavigationLinksProps) {
  const { user } = useAuth();
  const routerState = useRouterState();
  const currentPath = routerState.location.pathname;
  const roles = user?.roles ?? [];

  const visibleMainItems = mainMobileItems.filter((item) =>
    roles.some((role) => hasPermission(role, item.requiredPermission))
  );

  const visibleToolsItems = toolsMobileItems.filter((item) =>
    roles.some((role) => hasPermission(role, item.requiredPermission))
  );

  const visibleAdminItems = adminMobileItems.filter((item) =>
    roles.some((role) => hasPermission(role, item.requiredPermission))
  );

  return (
    <div className="p-4 space-y-1">
      {visibleMainItems.map((item) => {
        const Icon = item.icon;
        const isActive = currentPath === item.to;

        return (
          <Link
            key={item.to}
            to={item.to}
            onClick={onLinkClick}
            className={cn(
              'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
              isActive
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
            )}
          >
            <Icon className="h-5 w-5" />
            {item.label}
          </Link>
        );
      })}

      {visibleToolsItems.length > 0 && (
        <>
          <div className="pt-4 pb-2">
            <span className="px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Tools
            </span>
          </div>
          {visibleToolsItems.map((item) => {
            const Icon = item.icon;
            const isActive = currentPath === item.to;

            return (
              <Link
                key={item.to}
                to={item.to}
                onClick={onLinkClick}
                className={cn(
                  'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                )}
              >
                <Icon className="h-5 w-5" />
                {item.label}
              </Link>
            );
          })}
        </>
      )}

      {visibleAdminItems.length > 0 && (
        <>
          <div className="pt-4 pb-2">
            <span className="px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Admin
            </span>
          </div>
          {visibleAdminItems.map((item) => {
            const Icon = item.icon;
            const isActive = currentPath === item.to;

            return (
              <Link
                key={item.to}
                to={item.to}
                onClick={onLinkClick}
                className={cn(
                  'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                )}
              >
                <Icon className="h-5 w-5" />
                {item.label}
              </Link>
            );
          })}
        </>
      )}
    </div>
  );
}
